import { useCallback, useMemo, useRef } from "react";

// Package Imports
import { ColDef, DefaultMenuItem, GetContextMenuItemsParams, MenuItemDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import dayjs from "dayjs";

// Component Imports
import ActiveStateCell from "@/components/AGCellRenderers/ActiveStateCell";

// Types Imports
import { IAGStrategyDataRow } from "../StrategyTable.types";

export const useAGTable = ({ onDeleteStrategy }: { onDeleteStrategy: (row: IAGStrategyDataRow) => void }) => {
    const gridRef = useRef<AgGridReact<IAGStrategyDataRow> | null>(null);

    const colDefs = useMemo<ColDef<IAGStrategyDataRow>[]>(
        () => [
            {
                headerName: "Strategy ID",
                field: "strategy_id",
                cellClass: "ag-center-aligned-cell",
            },
            {
                headerName: "Display Name",
                field: "display_name",
                cellClass: "ag-center-aligned-cell",
            },
            {
                headerName: "Is Active",
                field: "is_active",
                cellClass: "ag-center-aligned-cell",
                cellRenderer: ActiveStateCell,
                cellRendererParams: (params: any) => ({
                    active_state: params.value,
                }),
            },
            {
                headerName: "Created At",
                field: "created",
                cellClass: "ag-center-aligned-cell",
                valueFormatter: (params: { value?: string }) =>
                    params.value ? dayjs(params.value).format("MM/DD/YYYY HH:mm:ss") : "",
            },
        ],
        []
    );

    const defaultColDef: ColDef = {
        flex: 1,
        filter: true,
        sortable: true,
    };

    const getContextMenuItems = useCallback(
        (params: GetContextMenuItemsParams): (MenuItemDef | DefaultMenuItem)[] => {
            const row = params.node?.data as IAGStrategyDataRow | undefined;

            const customItems: MenuItemDef[] = [
                {
                    name: "Delete",
                    action: () => row && onDeleteStrategy(row),
                    cssClasses: ["pulse-color--error-400"],
                },
            ];

            return [
                ...customItems,
                "separator",
                ...((params.defaultItems || []) as unknown as MenuItemDef[]),
            ];
        },
        [onDeleteStrategy]
    );

    const gridContext = useMemo(
        () => ({
            deleteStrategy: (row: IAGStrategyDataRow) => {
                onDeleteStrategy(row);
            },
        }),
        [onDeleteStrategy]
    );

    return {
        gridRef,
        colDefs,
        defaultColDef,
        gridContext,
        getContextMenuItems,
    };
}
